<template>
  <div class="container">
    <div class="brands__wrapper">
      <AppBreadcrumbs class="brands__breadcrumbs" />
      <div class="brands__header">
        <h1 class="brands__heading heading heading--4">
          <span>{{ t('All brands') }}</span>
        </h1>

        <section class="brands__alphabet">
          <div class="brands__alphabet--wrapper">
            <ul class="brands__alphabet--list">
              <li
                v-for="letter in 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('')"
                :key="letter"
              >
                <button
                  v-if="brandsData?.[letter]?.name"
                  class="brands__alphabet--button"
                  @click="scrollToLetter(anchorRefs[brandsData[letter].name])"
                >
                  <span>{{ letter }}</span>
                </button>
                <span
                  v-else
                  class="brands__alphabet--label"
                >
                  <span>{{ letter }}</span>
                </span>
              </li>
            </ul>
          </div>
        </section>
      </div>

      <section class="brands__grid">
        <div
          v-for="(item, i) in brandsData"
          :ref="(el) => (anchorRefs[item.name] = el)"
          :key="`${i}-${!i}-${showAll}`"
          class="brands__grid__column"
        >
          <UiCollapsable
            :label="item.name"
            :open="showAll"
            data-name="brand-list"
          >
            <UiReadMore
              :is-disabled="!showAll"
              :more="t('More brands')"
              :less="t('Less brands')"
            >
              <ul class="brands__grid__column--menu">
                <li
                  v-for="(brand, j) in item.brands"
                  :key="j"
                  class="brands__grid__column--item"
                >
                  <AppLink
                    :href="'/' + brand.url"
                    class="brands__grid__column--link"
                  >
                    {{ brand.label }}
                  </AppLink>

                  <span
                    v-if="brand.is_new"
                    class="brands__grid__column--new"
                  >
                    nieuw
                  </span>
                </li>
              </ul>
            </UiReadMore>
            <template #icon_after="{ open }">
              <IconPlus
                v-if="!open"
                class="collapsable__icon icon icon--arrow"
              />
              <IconMin
                v-else
                class="collapsable__icon icon icon--arrow"
              />
            </template>
          </UiCollapsable>
        </div>
      </section>
    </div>
  </div>
</template>

<script lang="ts" setup>
const { t } = useI18n({ useScope: 'global' })
const { setBreadcrumbs } = useBreadcrumbs()
const { pageView } = useDataLayer()
const anchorRefs = ref({})
const brandsData = ref<any[]>([])
const { fetchBrands, brands } = useBrands()
const { breakpoint } = useApp()
const showAll = ref(true)
const getLayoutHeaderHeight = inject('getLayoutHeaderHeight', () => 0)

setBreadcrumbs({
  label: t('All brands'),
})

pageView('brands')

const metaTitle = computed(
  () => t('All brands'),
)

const metaDescription = computed(
  () => metaTitle.value + ' | ' + t('nuxtSiteConfig.description'),
)

useHead({
  title: metaTitle,
  meta: [
    { hid: 'description', name: 'description', content: metaDescription },
    { hid: 'robots', name: 'robots', content: 'index, follow' },
  ],
})

const loadBrands = async () => {
  await fetchBrands()
  brandsData.value = [...(brands.value?.brandsList?.urls || [])].sort((a, b) => {
    const aValue = a.name?.toUpperCase() // ignore casing
    const bValue = b.name?.toUpperCase() // ignore casing
    if (aValue < bValue) {
      return -1
    }
    if (aValue > bValue) {
      return 1
    }
    return 0
  }).reduce(function (groups, brand) {
    const firstLetter = brand.name?.toUpperCase()?.charAt(0)
    if (!groups[firstLetter]) {
      groups[firstLetter] = {
        name: firstLetter,
        brands: [],
      }
    }

    groups[firstLetter].brands.push(brand)

    return groups
  }, {})
}
loadBrands()

const scrollToLetter = (el: HTMLElement) => {
  const offsetHeader = getLayoutHeaderHeight() + 1
  const elementLocation = el.getBoundingClientRect().top + window.scrollY
  window.scrollTo({ top: elementLocation - offsetHeader, behavior: 'smooth' })
}

onMounted(() => {
  watchEffect(() => {
    showAll.value = !['mobile', 'phablet'].includes(breakpoint.value)
  })
})
</script>

<style src="~layers/app/pages/~brands.css" />
